import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaDownload, FaArrowLeft, FaPrint, FaCheckCircle, FaFileInvoice } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

interface Fee {
  id: number;
  student_name: string;
  month: string;
  amount_paid: string;
  date_paid: string;
  class_name: string;
  balance: string;
  total_amount: string;
  collected_by: string;
  receipt_number?: number;
  payment_mode?: string;
  discount?: string;
}

const DetailFees: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state: RootState) => state.auth.value);
  const receiptRef = useRef<HTMLDivElement>(null);

  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    branchName: '',
    Address: '',
    contactNumber: '',
  });
  const [student, setStudent] = useState<Fee | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { id } = useParams<{ id: string }>();

  const getCookie = (name: string) => {
    const cookieName = name + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          setError('No authentication token found. Please log in.');
          setLoading(false);
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('/users/user');
        setUserInfo({
          firstName: data.user.first_name,
          lastName: data.user.last_name,
          branchName: data.user.branch_name,
          Address: data.user.address,
          contactNumber: data.user.contact_number,
        });
        console.log("User role:", data.user.role);
        dispatch(setAuth(true));
      } catch (error: any) {
        dispatch(setAuth(false));
        setError(error.response?.data?.detail || 'Failed to fetch user data');
      } finally {
        setLoading(false);
      }
    };

    const getSingleStudent = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          setError('No authentication token found. Please log in.');
          setLoading(false);
          return;
        }
        const { data } = await axios.get<Fee>(`/fees/fees/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStudent(data);
      } catch (error: any) {
        console.error('Error fetching fee details:', error);
        setError(error.response?.data?.detail || 'Failed to fetch fee details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    getSingleStudent();
  }, [dispatch, id]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const parseCurrency = (value: string): number => {
    if (!value) return 0;
    const cleaned = value.toString().replace(/Ksh\s*/gi, '').replace(/,/g, '').trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  };

  const getPaymentStatus = (balance: string) => {
    const bal = parseCurrency(balance);
    return bal <= 0 ? 'Waa la wada bixiyay' : 'Qeyb ayaa la bixiyay';
  };

  const downloadInvoice = async () => {
    if (!student) return;

    const doc = new jsPDF();
    const logoPath = '/sanabiland-logo.jpg';

    // Add school logo
    try {
      const img = new Image();
      img.src = logoPath;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve;
      });
      doc.addImage(img, 'JPEG', 85, 10, 40, 40);
    } catch (error) {
      console.error('Error loading logo:', error);
    }

    // Header with school name
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('Sanabil Schools', 105, 60, { align: 'center' });

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    doc.text(userInfo.Address || 'Ciwaanka Iskuulka', 105, 67, { align: 'center' });
    doc.text(`Tel: ${userInfo.contactNumber || 'N/A'}`, 105, 73, { align: 'center' });

    // Divider line
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.5);
    doc.line(20, 80, 190, 80);

    // Receipt title with colored background
    doc.setFillColor(99, 102, 241);
    doc.rect(20, 85, 170, 12, 'F');
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('RASIIDKA LACAG BIXINTA', 105, 93, { align: 'center' });

    // Receipt details
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(`Rasiidka Lr: ${student.id || 'N/A'}`, 20, 105);
    doc.text(`Taariikhda: ${formatDate(student.date_paid)}`, 140, 105);
    doc.text(`Heerka Lacag Bixinta: ${getPaymentStatus(student.balance)}`, 20, 112);

    // Student details table
    (doc as any).autoTable({
      startY: 120,
      head: [['Macluumaadka Ardayga', '']],
      body: [
        ['Magaca Ardayga', student.student_name || 'N/A'],
        ['Fasalka', student.class_name || 'N/A'],
        ['Bisha', student.month || 'N/A'],
      ],
      theme: 'plain',
      headStyles: {
        fillColor: [243, 244, 246],
        textColor: [31, 41, 55],
        fontStyle: 'bold',
        fontSize: 11
      },
      bodyStyles: { fontSize: 10, textColor: [55, 65, 81] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 110 }
      },
      margin: { left: 20, right: 20 },
    });

    const finalY1 = (doc as any).lastAutoTable.finalY + 5;

    // Payment details table
    (doc as any).autoTable({
      startY: finalY1,
      head: [['Faahfaahinta Lacag Bixinta', 'Cadadka (KSh)']],
      body: [
        ['Wadarta Guud', student.total_amount || '0'],
        ['Sicir Dhimis', student.discount || '0'],
        ['Lacagta La Bixiyay', student.amount_paid || '0'],
        ['Haraaga Harsan', student.balance || '0'],
      ],
      theme: 'striped',
      headStyles: {
        fillColor: [99, 102, 241],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 11
      },
      bodyStyles: { fontSize: 10 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 100 },
        1: { halign: 'right', cellWidth: 70 }
      },
      margin: { left: 20, right: 20 },
      didParseCell: function (data: any) {
        if (data.row.index === 3) {
          data.cell.styles.fillColor = parseCurrency(student.balance) > 0 ? [254, 226, 226] : [220, 252, 231];
          data.cell.styles.textColor = parseCurrency(student.balance) > 0 ? [185, 28, 28] : [21, 128, 61];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    const finalY2 = (doc as any).lastAutoTable.finalY + 10;

    // Collected by
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(`Waxaa Ururiyay: ${student.collected_by || 'N/A'}`, 20, finalY2);

    // Footer
    doc.setDrawColor(229, 231, 235);
    doc.line(20, finalY2 + 10, 190, finalY2 + 10);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(99, 102, 241);
    doc.text('Waad ku mahadsan tahay lacag bixintaada!', 105, finalY2 + 20, { align: 'center' });

    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text('Kan waa rasiid kumbuyuutar lagu sameeyay uma baahna saxiix.', 105, finalY2 + 27, { align: 'center' });

    doc.save(`Fee_Receipt_${student.student_name}_${student.month}.pdf`);
    setSuccessMessage('Rasiidka si guul leh ayaa loo soo degsaday!');
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/fees');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Soo dejinaya...</span>
          </div>
          <p className="mt-3 text-muted">Soo dejinaya rasiidka...</p>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <h4 className="text-muted mb-3">Ma aadan xaqiijin</h4>
          <a href="/login" className="btn btn-primary px-4 py-2">Aad Login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 min-vh-100 w-full lg:ml-[250px] lg:w-[calc(100%-250px)]" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-6">
          {/* Action Buttons */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={handleBack}>
              <FaArrowLeft /> Ku Noqo Lacagaha
            </button>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={handlePrint}>
                <FaPrint /> Daabac
              </button>
              <button className="btn btn-success d-flex align-items-center gap-2" onClick={downloadInvoice} disabled={!student}>
                <FaDownload /> Soo Degso PDF
              </button>
            </div>
          </div>

          {/* Alerts */}
          {successMessage && (
            <div className="alert alert-success alert-dismissible fade show shadow-sm border-0 mb-4" role="alert">
              <FaCheckCircle className="me-2" />
              <strong>Guul!</strong> {successMessage}
              <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
            </div>
          )}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show shadow-sm border-0 mb-4" role="alert">
              <strong>Khalad!</strong> {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {/* Receipt Card */}
          <div ref={receiptRef} className="card border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
            {/* Header with School Branding */}
            <div className="text-center py-5 position-relative" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <img
                src="/sanabiland-logo.jpg"
                alt="Sanabil Schools Logo"
                className="mb-3"
                style={{ width: '100px', height: '100px', objectFit: 'contain', borderRadius: '50%', background: 'white', padding: '10px' }}
              />
              <h3 className="text-white fw-bold mb-1">Sanabil Schools</h3>
              <p className="text-white opacity-75 mb-1" style={{ fontSize: '14px' }}>
                {userInfo.Address || 'Ciwaanka Iskuulka'}
              </p>
              <p className="text-white opacity-75 mb-0" style={{ fontSize: '14px' }}>
                Tel: {userInfo.contactNumber || 'N/A'}
              </p>
            </div>

            {/* Receipt Content */}
            <div className="card-body p-4 p-md-5">
              {!student ? (
                <p className="text-center text-muted">Faahfaahinta lacagta lama helin</p>
              ) : (
                <>
                  {/* Receipt Title */}
                  <div className="text-center mb-4">
                    <div className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill mb-3" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                      <FaFileInvoice className="text-white" />
                      <span className="text-white fw-bold">RASIIDKA LACAG BIXINTA</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Rasiidka Lr: <strong>#{student.id}</strong></span>
                      <span className={`badge ${parseCurrency(student.balance) <= 0 ? 'bg-success' : 'bg-warning'} px-3 py-2`}>
                        {getPaymentStatus(student.balance)}
                      </span>
                    </div>
                    <p className="text-muted mb-0 mt-1">Taariikhda: {formatDate(student.date_paid)}</p>
                  </div>

                  <hr className="my-4" />

                  {/* Student Information */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3" style={{ color: '#667eea' }}>
                      <i className="bi bi-person-fill me-2"></i>Macluumaadka Ardayga
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                          <small className="text-muted d-block mb-1">Magaca Ardayga</small>
                          <strong>{student.student_name || 'N/A'}</strong>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                          <small className="text-muted d-block mb-1">Fasalka</small>
                          <strong>{student.class_name || 'N/A'}</strong>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                          <small className="text-muted d-block mb-1">Bisha</small>
                          <strong>{student.month || 'N/A'}</strong>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                          <small className="text-muted d-block mb-1">Waxaa Ururiyay</small>
                          <strong>{student.collected_by || 'N/A'}</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  {/* Payment Details */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3" style={{ color: '#667eea' }}>
                      <i className="bi bi-cash-stack me-2"></i>Faahfaahinta Lacag Bixinta
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted">Wadarta Guud</td>
                            <td className="text-end fw-medium">{student.total_amount || 'KSh 0'}</td>
                          </tr>
                          {student.discount && parseCurrency(student.discount) > 0 && (
                            <tr>
                              <td className="text-muted">Sicir Dhimis</td>
                              <td className="text-end fw-medium text-success">- {student.discount || 'KSh 0'}</td>
                            </tr>
                          )}
                          <tr>
                            <td className="text-muted">Lacagta La Bixiyay</td>
                            <td className="text-end fw-medium text-primary">{student.amount_paid || 'KSh 0'}</td>
                          </tr>
                          <tr className="border-top">
                            <td className="fw-bold pt-3">Haraaga Harsan</td>
                            <td className={`text-end fw-bold pt-3 ${parseCurrency(student.balance) > 0 ? 'text-danger' : 'text-success'}`}>
                              {student.balance || 'KSh 0'}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Footer Note */}
                  <div className="text-center mt-4 pt-4 border-top">
                    <p className="text-muted mb-0" style={{ fontSize: '14px', fontStyle: 'italic' }}>
                      Waad ku mahadsan tahay lacag bixintaada! Kan waa rasiid kumbuyuutar lagu sameeyay.
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .btn, .alert { display: none !important; }
          body { background: white !important; }
          .card { box-shadow: none !important; }
        }
        
        .card:hover {
          transform: translateY(-2px);
          transition: all 0.3s ease;
        }
        
        @media (max-width: 768px) {
          .d-flex.gap-2 {
            flex-direction: column;
            width: 100%;
          }
          .d-flex.gap-2 .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default DetailFees;